// src/componentForms/ContactFormForm.js

import React, { useEffect, useRef } from 'react';
import { Form, Button, Tabs, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

const ContactFormForm = ({ formValues, setFormValues }) => {

  console.log(formValues);

  // Generate a unique default form name only once
  const defaultFormName = useRef(`contactform_${Math.floor(Math.random() * 10000)}`);

  // ---------- helpers ----------
  const setFV = (patch) => setFormValues(prev => ({ ...prev, ...patch }));

  const ensureArray = (val, fallback = []) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim().length) {
      return val.split(',').map(s => s.trim()).filter(Boolean);
    }
    return fallback;
  };

  const syncRecipientsCsv = (arr) => (arr || []).join(',');

  // If props might come as a JSON string, normalize once
  useEffect(() => {
    const maybeParse = (v) => {
      if (typeof v === 'string') {
        try { return JSON.parse(v); } catch (e) { return v; }
      }
      return v;
    };

    if (typeof formValues === 'string') {
      const parsed = maybeParse(formValues);
      if (parsed && typeof parsed === 'object') setFormValues(parsed);
    }

    if (formValues && typeof formValues === 'object' && typeof formValues.props === 'string') {
      const parsed = maybeParse(formValues.props);
      if (parsed && typeof parsed === 'object') setFormValues(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build a derived "notifications" object for rendering convenience only.
  const notifications = {
    enabled: !!(formValues && formValues.notify_enabled),
    transport: (formValues && formValues.notify_transport) || 'smtp',
    recipients: ensureArray(
      formValues && formValues.notify_recipients,
      (formValues && formValues.notify_recipients_csv)
        ? ensureArray(formValues.notify_recipients_csv)
        : ['']
    ),
    subjectTemplate:
      (formValues && formValues.notify_subject_template)
        ? formValues.notify_subject_template
        : 'New contact submission — $formName',
    bodyIntro:
      (formValues && formValues.notify_body_intro)
        ? formValues.notify_body_intro
        : 'You have received a new contact form submission.',
    includeSubmissionTable:
      (formValues && typeof formValues.notify_include_table !== 'undefined')
        ? formValues.notify_include_table
        : true,
    smtp: {
      host: (formValues && formValues.smtp_host) != null ? formValues.smtp_host : '',
      port: (formValues && formValues.smtp_port) != null ? formValues.smtp_port : 587,
      encryption: (formValues && formValues.smtp_encryption) ? formValues.smtp_encryption : 'tls',
      username: (formValues && formValues.smtp_username) != null ? formValues.smtp_username : '',
      password: (formValues && formValues.smtp_password) != null ? formValues.smtp_password : '',
      fromEmail: (formValues && formValues.smtp_from_email) != null ? formValues.smtp_from_email : '',
      fromName: (formValues && formValues.smtp_from_name) != null ? formValues.smtp_from_name : '',
      replyTo: (formValues && formValues.smtp_reply_to) != null ? formValues.smtp_reply_to : ''
    }
  };

  // ---------- initialize defaults on mount (only when truly undefined) ----------
  useEffect(() => {
    const patch = {};

    // base
    if (formValues.formName === undefined) patch.formName = defaultFormName.current;
    if (formValues.buttonText === undefined) patch.buttonText = 'Submit';
    if (formValues.validationMessages === undefined) {
      patch.validationMessages = {
        required: '$field is required.',
        email: '$field must be a valid email address.',
        number: '$field must be a valid number.'
      };
    }
    if (formValues.successMessage === undefined) patch.successMessage = 'Form submitted successfully!';

    // flat notification defaults
    if (formValues.notify_enabled === undefined) patch.notify_enabled = false;
    if (formValues.notify_transport === undefined) patch.notify_transport = 'smtp';
    if (formValues.notify_recipients === undefined && formValues.notify_recipients_csv === undefined) {
      patch.notify_recipients = [''];
      patch.notify_recipients_csv = '';
    }
    if (formValues.notify_subject_template === undefined) {
      patch.notify_subject_template = 'New contact submission — $formName';
    }
    if (formValues.notify_body_intro === undefined) {
      patch.notify_body_intro = 'You have received a new contact form submission.';
    }
    if (formValues.notify_include_table === undefined) patch.notify_include_table = true;

    // smtp defaults
    if (formValues.smtp_host === undefined) patch.smtp_host = '';
    if (formValues.smtp_port === undefined) patch.smtp_port = 587;
    if (formValues.smtp_encryption === undefined) patch.smtp_encryption = 'tls';
    if (formValues.smtp_username === undefined) patch.smtp_username = '';
    if (formValues.smtp_password === undefined) patch.smtp_password = '';
    if (formValues.smtp_from_email === undefined) patch.smtp_from_email = '';
    if (formValues.smtp_from_name === undefined) patch.smtp_from_name = '';
    if (formValues.smtp_reply_to === undefined) patch.smtp_reply_to = '';

    if (Object.keys(patch).length) setFV(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- existing fields tab handlers ----------
  const handleFieldChange = (index, key, value) => {
    const original = Array.isArray(formValues.formFields) ? formValues.formFields : [];
    const updated = [...original];
    const prev = updated[index] || {};
    updated[index] = { ...prev, [key]: value };
    setFV({ formFields: updated });
  };

  const handleAddField = () => {
    const newField = {
      type: 'text',
      label: 'New Field',
      name: `field_${Date.now()}`,
      placeholder: '',
      required: false
    };
    const original = Array.isArray(formValues.formFields) ? formValues.formFields : [];
    setFV({ formFields: [...original, newField] });
  };

  const handleRemoveField = (index) => {
    const original = Array.isArray(formValues.formFields) ? formValues.formFields : [];
    const updated = original.filter((_, i) => i !== index);
    setFV({ formFields: updated });
  };

  // ---------- notifications tab handlers (bind to flat keys) ----------
  const setNotify = (patch) => setFV(patch);
  const setSMTP = (patch) => setFV(patch);

  const handleRecipientChange = (idx, value) => {
    const arr = [...notifications.recipients];
    arr[idx] = value;
    setNotify({
      notify_recipients: arr,
      notify_recipients_csv: syncRecipientsCsv(arr)
    });
  };

  const addRecipient = () => {
    const arr = [...notifications.recipients, ''];
    setNotify({
      notify_recipients: arr,
      notify_recipients_csv: syncRecipientsCsv(arr)
    });
  };

  const removeRecipient = (idx) => {
    const arr = [...notifications.recipients].filter((_, i) => i !== idx);
    const next = arr.length ? arr : [''];
    setNotify({
      notify_recipients: next,
      notify_recipients_csv: syncRecipientsCsv(next)
    });
  };

  return (
    <Tabs defaultActiveKey="fields" className="mb-3">
      {/* TAB 1: Fields & Messages */}
      <Tab eventKey="fields" title="Fields & Messages">
        <Form>
          <Form.Group className="mb-4 carousel-wrap pb-3">
            <Form.Label>Form Name</Form.Label>
            <Form.Control
              type="text"
              value={(formValues.formName != null) ? formValues.formName : defaultFormName.current}
              onChange={(e) => setFV({ formName: e.target.value })}
              placeholder="Enter Form Name"
            />
          </Form.Group>

          <Form.Group className="mb-4 carousel-wrap pb-3">
            <Form.Label>Button Text</Form.Label>
            <Form.Control
              type="text"
              value={(formValues.buttonText != null) ? formValues.buttonText : 'Submit'}
              onChange={(e) => setFV({ buttonText: e.target.value })}
              placeholder="Enter button text"
            />
          </Form.Group>

          {(formValues.formFields || []).map((field = {}, index) => (
            <div key={index} className="mb-4 carousel-wrap">
              <div className="row">
                <Form.Group className="mb-2 col-md-6">
                  <Form.Label>Field Type</Form.Label>
                  <Form.Select
                    value={field.type || 'text'}
                    onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                  >
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="textarea">Textarea</option>
                    <option value="number">Number</option>
                    <option value="password">Password</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2 col-md-6">
                  <Form.Label>Label</Form.Label>
                  <Form.Control
                    type="text"
                    value={(field.label != null) ? field.label : ''}
                    onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2 col-md-6">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={(field.name != null) ? field.name : ''}
                    onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2 col-md-6">
                  <Form.Label>Placeholder</Form.Label>
                  <Form.Control
                    type="text"
                    value={(field.placeholder != null) ? field.placeholder : ''}
                    onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2 col-md-6">
                  <Form.Check
                    type="checkbox"
                    label="Required"
                    checked={!!field.required}
                    onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                  />
                </Form.Group>
              </div>
              <Button variant="danger" onClick={() => handleRemoveField(index)} className="mt-2">
                <FontAwesomeIcon icon={faTrash} /> Remove Field
              </Button>
            </div>
          ))}

          <Button variant="primary" className="add-more-media" onClick={handleAddField}>
            <FontAwesomeIcon icon={faPlus} /> Add Field
          </Button>

          {/* Custom Validation Messages */}
          <div className="mb-4 carousel-wrap">
            <div className="row">
              <Form.Group className="mb-2 col-md-12">
                <Form.Label>Required Field Message</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    (formValues.validationMessages && formValues.validationMessages.required != null)
                      ? formValues.validationMessages.required
                      : '$field is required.'
                  }
                  onChange={(e) =>
                    setFV({
                      validationMessages: {
                        ...(formValues.validationMessages || {}),
                        required: e.target.value
                      }
                    })
                  }
                  placeholder="Enter custom required field message (use $field for field name)"
                />
              </Form.Group>

              <Form.Group className="mb-2 col-md-12">
                <Form.Label>Email Field Message</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    (formValues.validationMessages && formValues.validationMessages.email != null)
                      ? formValues.validationMessages.email
                      : '$field must be a valid email address.'
                  }
                  onChange={(e) =>
                    setFV({
                      validationMessages: {
                        ...(formValues.validationMessages || {}),
                        email: e.target.value
                      }
                    })
                  }
                  placeholder="Enter custom email field message (use $field for field name)"
                />
              </Form.Group>

              <Form.Group className="mb-2 col-md-12">
                <Form.Label>Number Field Message</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    (formValues.validationMessages && formValues.validationMessages.number != null)
                      ? formValues.validationMessages.number
                      : '$field must be a valid number.'
                  }
                  onChange={(e) =>
                    setFV({
                      validationMessages: {
                        ...(formValues.validationMessages || {}),
                        number: e.target.value
                      }
                    })
                  }
                  placeholder="Enter custom number field message (use $field for field name)"
                />
              </Form.Group>

              {/* Custom Success Message */}
              <Form.Group className="mb-2 col-md-12">
                <Form.Label>Success Message</Form.Label>
                <Form.Control
                  type="text"
                  value={(formValues.successMessage != null) ? formValues.successMessage : 'Form submitted successfully!'}
                  onChange={(e) => setFV({ successMessage: e.target.value })}
                  placeholder="Enter custom success message"
                />
              </Form.Group>
            </div>
          </div>
        </Form>
      </Tab>

      {/* TAB 2: Email Notifications (flat bindings) */}
      <Tab eventKey="notifications" title="Email Notifications">
        <Form>
          <div className="mb-4 carousel-wrap pb-3">
            <Form.Check
              type="switch"
              id="notifications-enabled"
              label="Enable email notifications"
              checked={!!(formValues && formValues.notify_enabled)}
              onChange={(e) => setNotify({ notify_enabled: e.target.checked })}
            />
            <Form.Text muted>
              Emails will be sent using the  SMTP credentials.
            </Form.Text>
          </div>

          {formValues && formValues.notify_enabled ? (
            <>
              <div className="mb-4 carousel-wrap">
                <Form.Label className="fw-bold">Recipients</Form.Label>
                {notifications.recipients.map((rcpt, idx) => (
                  <div key={idx} className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="email"
                      placeholder="recipient@example.com"
                      value={(rcpt != null) ? rcpt : ''}
                      onChange={(e) => handleRecipientChange(idx, e.target.value)}
                    />
                    <Button variant="outline-danger" onClick={() => removeRecipient(idx)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline-primary" onClick={addRecipient}>
                  <FontAwesomeIcon icon={faPlus} /> Add recipient
                </Button>
                <Form.Text muted className="d-block mt-1">
                  We recommend adding at least one owner/manager email here.
                </Form.Text>
              </div>

              <div className="mb-4 carousel-wrap">
                <Form.Label className="fw-bold">Email Content</Form.Label>
                <Form.Group className="mb-2">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={(formValues.notify_subject_template != null) ? formValues.notify_subject_template : ''}
                    onChange={(e) => setNotify({ notify_subject_template: e.target.value })}
                    placeholder="New contact submission — $formName"
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Intro / Header Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={(formValues.notify_body_intro != null) ? formValues.notify_body_intro : ''}
                    onChange={(e) => setNotify({ notify_body_intro: e.target.value })}
                    placeholder="You have received a new contact form submission."
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Include auto-generated submission table"
                    checked={!!formValues.notify_include_table}
                    onChange={(e) => setNotify({ notify_include_table: e.target.checked })}
                  />
                </Form.Group>
              </div>

              <div className="mb-4 carousel-wrap">
                <Form.Label className="fw-bold">SMTP Settings</Form.Label>
                <div className="row">
                  <Form.Group className="mb-2 col-md-6">
                    <Form.Label>SMTP Host</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="smtp.yourdomain.com"
                      value={(formValues.smtp_host != null) ? formValues.smtp_host : ''}
                      onChange={(e) => setSMTP({ smtp_host: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3">
                    <Form.Label>Port</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      placeholder="587"
                      value={Number((formValues.smtp_port != null) ? formValues.smtp_port : 587)}
                      onChange={(e) => setSMTP({ smtp_port: Number(e.target.value || 0) })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3">
                    <Form.Label>Encryption</Form.Label>
                    <Form.Select
                      value={(formValues.smtp_encryption && formValues.smtp_encryption.length) ? formValues.smtp_encryption : 'tls'}
                      onChange={(e) => setSMTP({ smtp_encryption: e.target.value })}
                    >
                      <option value="none">None</option>
                      <option value="ssl">SSL</option>
                      <option value="tls">TLS</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-6">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="email@yourdomain.com"
                      value={(formValues.smtp_username != null) ? formValues.smtp_username : ''}
                      onChange={(e) => setSMTP({ smtp_username: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-6">
                    <Form.Label>Password / App Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="••••••••"
                      value={(formValues.smtp_password != null) ? formValues.smtp_password : ''}
                      onChange={(e) => setSMTP({ smtp_password: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-6">
                    <Form.Label>From Email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="email@yourdomain.com"
                      value={(formValues.smtp_from_email != null) ? formValues.smtp_from_email : ''}
                      onChange={(e) => setSMTP({ smtp_from_email: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-6">
                    <Form.Label>From Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Your Site / Company"
                      value={(formValues.smtp_from_name != null) ? formValues.smtp_from_name : ''}
                      onChange={(e) => setSMTP({ smtp_from_name: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-6">
                    <Form.Label>Reply-To (optional)</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="reply@yourdomain.com"
                      value={(formValues.smtp_reply_to != null) ? formValues.smtp_reply_to : ''}
                      onChange={(e) => setSMTP({ smtp_reply_to: e.target.value })}
                    />
                  </Form.Group>
                </div>
              </div>

              {/* Optional: Test button */}
              <div className="mb-4">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    const payload = { ...formValues };
                    console.log('Send Test Email with flat config:', payload);
                    alert('Test email trigger — wire this up to your backend with flat fields.');
                  }}
                >
                  Send Test Email
                </Button>
              </div>
            </>
          ) : null}
        </Form>
      </Tab>
    </Tabs>
  );
};

export default ContactFormForm;
